/**
 * Notifications Management - Admin Page
 * จัดการการส่ง notifications ให้กับ users
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  FiBell,
  FiSend,
  FiUsers,
  FiAlertCircle,
  FiRefreshCw,
  FiPlus,
  FiFilter,
  FiSearch,
  FiCheckCircle,
  FiXCircle,
  FiInfo
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import { AdminLayout } from '../../components/admin';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { Button } from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import CreateNotificationModal from '../../components/admin/notifications/CreateNotificationModal';
import toast from 'react-hot-toast';
import { API_URL } from '../../services/api';

// Types
enum NotificationType {
  INFO = 'INFO',
  SUCCESS = 'SUCCESS',
  WARNING = 'WARNING',
  ERROR = 'ERROR'
}

enum TargetUsers {
  ALL = 'ALL',
  ADMINS = 'ADMINS',
  MEMBERS = 'MEMBERS',
  SPECIFIC = 'SPECIFIC'
}

interface Notification {
  id: number;
  title: string;
  message: string;
  notification_type: NotificationType;
  target_users: TargetUsers;
  specific_user_ids?: string[];
  created_by: string;
  is_sent: boolean;
  sent_at?: string;
  created_at: string;
}

interface CreateNotificationData {
  title: string;
  message: string;
  notification_type: NotificationType;
  target_users: TargetUsers;
  specific_user_ids?: string[];
}

interface NotificationStats {
  total_sent: number;
  total_pending: number;
  total_recipients: number;
}

const NotificationsManagement: React.FC = () => {
  const { token } = useAdminAuth();
  const [errorState, { handleError, clearError }] = useErrorHandler();

  // State
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [stats, setStats] = useState<NotificationStats>({
    total_sent: 0,
    total_pending: 0,
    total_recipients: 0
  });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<NotificationType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'sent' | 'pending'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // API call function
  const callApi = useCallback(async <T = any>(options: {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    endpoint: string;
    data?: any;
  }): Promise<{ success: boolean; data: T; message?: string }> => {
    if (!token) {
      throw new Error('ไม่พบ token สำหรับการยืนยันตัวตน');
    }

    
    const response = await fetch(`${API_URL}${options.endpoint}`, {
      method: options.method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: options.data ? JSON.stringify(options.data) : undefined,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.detail || result.message || `HTTP ${response.status}`);
    }

    return {
      success: true,
      data: result.data || result, // Extract data from nested structure
      message: result.message,
      pagination: result.pagination
    };
  }, [token]);

  // Load notifications
  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      clearError();

      const response = await callApi<Notification[]>({
        method: 'GET',
        endpoint: '/api/admin/notifications'
      });

      if (response.success && response.data) {
        setNotifications(response.data);

        // Calculate stats
        const sent = response.data.filter(n => n.is_sent).length;
        const pending = response.data.filter(n => !n.is_sent).length;
        setStats({
          total_sent: sent,
          total_pending: pending,
          total_recipients: response.data.length
        });
      }
    } catch (error) {
      handleError(error, 'ไม่สามารถโหลดข้อมูล notifications ได้');
    } finally {
      setLoading(false);
    }
  }, [callApi, handleError, clearError]);

  // Initial load
  useEffect(() => {
    if (token) {
      loadNotifications();
    }
  }, [loadNotifications, token]);

  // Filter notifications
  const filteredNotifications = notifications.filter((notification) => {
    // Search filter
    const matchesSearch =
      notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notification.message.toLowerCase().includes(searchTerm.toLowerCase());

    // Type filter
    const matchesType = typeFilter === 'all' || notification.notification_type === typeFilter;

    // Status filter
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'sent' && notification.is_sent) ||
      (statusFilter === 'pending' && !notification.is_sent);

    return matchesSearch && matchesType && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const paginatedNotifications = filteredNotifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Type badge color
  const getTypeBadgeColor = (type: NotificationType) => {
    switch (type) {
      case NotificationType.INFO:
        return 'bg-blue-100 text-blue-800';
      case NotificationType.SUCCESS:
        return 'bg-green-100 text-green-800';
      case NotificationType.WARNING:
        return 'bg-yellow-100 text-yellow-800';
      case NotificationType.ERROR:
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Target users badge color
  const getTargetBadgeColor = (target: TargetUsers) => {
    switch (target) {
      case TargetUsers.ALL:
        return 'bg-purple-100 text-purple-800';
      case TargetUsers.ADMINS:
        return 'bg-indigo-100 text-indigo-800';
      case TargetUsers.MEMBERS:
        return 'bg-teal-100 text-teal-800';
      case TargetUsers.SPECIFIC:
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Target users label
  const getTargetLabel = (target: TargetUsers) => {
    switch (target) {
      case TargetUsers.ALL:
        return 'ทั้งหมด';
      case TargetUsers.ADMINS:
        return 'ผู้ดูแลระบบ';
      case TargetUsers.MEMBERS:
        return 'สมาชิก';
      case TargetUsers.SPECIFIC:
        return 'เฉพาะเจาะจง';
      default:
        return target;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <FiBell className="mr-3 h-8 w-8 text-blue-600" />
                จัดการการแจ้งเตือน
              </h1>
              <p className="mt-1 text-sm text-gray-600">
                สร้างและจัดการการแจ้งเตือนสำหรับผู้ใช้งาน
              </p>
            </div>
            <div className="flex space-x-3">
              <Button
                variant="secondary"
                leftIcon={<FiRefreshCw />}
                onClick={() => loadNotifications()}
                disabled={loading}
              >
                รีเฟรช
              </Button>
              <Button
                variant="default"
                leftIcon={<FiPlus />}
                onClick={() => setShowModal(true)}
              >
                สร้างการแจ้งเตือน
              </Button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorState.message && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            <p className="font-medium">เกิดข้อผิดพลาด</p>
            <p className="text-sm">{errorState.message}</p>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-green-100">
                    <FiCheckCircle className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">ส่งแล้ว</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_sent}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-yellow-100">
                    <FiAlertCircle className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">รอดำเนินการ</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_pending}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100">
                    <FiUsers className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">ผู้รับทั้งหมด</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_recipients}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="ค้นหาการแจ้งเตือน..."
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Type Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value as NotificationType | 'all')}
              >
                <option value="all">ประเภททั้งหมด</option>
                <option value={NotificationType.INFO}>ข้อมูล</option>
                <option value={NotificationType.SUCCESS}>สำเร็จ</option>
                <option value={NotificationType.WARNING}>คำเตือน</option>
                <option value={NotificationType.ERROR}>ข้อผิดพลาด</option>
              </select>

              {/* Status Filter */}
              <select
                className="px-3 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as 'all' | 'sent' | 'pending')}
              >
                <option value="all">สถานะทั้งหมด</option>
                <option value="sent">ส่งแล้ว</option>
                <option value="pending">รอดำเนินการ</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Notifications List - Dual View Pattern */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <FiRefreshCw className="animate-spin h-8 w-8 text-blue-600" />
                <span className="ml-2 text-gray-600">กำลังโหลด...</span>
              </div>
            ) : paginatedNotifications.length === 0 ? (
              <div className="text-center py-12">
                <FiBell className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {searchTerm ? 'ไม่พบการแจ้งเตือนที่ค้นหา' : 'ยังไม่มีการแจ้งเตือน'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {searchTerm ? 'ลองเปลี่ยนคำค้นหา' : 'เริ่มต้นด้วยการสร้างการแจ้งเตือนใหม่'}
                </p>
                {!searchTerm && (
                  <Button
                    variant="default"
                    leftIcon={<FiPlus />}
                    onClick={() => setShowModal(true)}
                    className="mt-4"
                  >
                    สร้างการแจ้งเตือนแรก
                  </Button>
                )}
              </div>
            ) : (
              <>
                {/* Mobile Cards View - Hidden on Desktop */}
                <div className="md:hidden divide-y divide-gray-200">
                  {paginatedNotifications.map((notification, index) => (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                      className="p-4 hover:bg-gray-50"
                    >
                      {/* Title & Status Row */}
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900 flex-1 pr-2">
                          {notification.title}
                        </h3>
                        {notification.is_sent ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 flex-shrink-0">
                            <FiCheckCircle className="mr-1 h-3 w-3" />
                            ส่งแล้ว
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                            <FiAlertCircle className="mr-1 h-3 w-3" />
                            รอดำเนินการ
                          </span>
                        )}
                      </div>

                      {/* Message */}
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {notification.message}
                      </p>

                      {/* Details Grid */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">ประเภท:</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(notification.notification_type)}`}>
                            {notification.notification_type === NotificationType.INFO && <FiInfo className="mr-1 h-3 w-3" />}
                            {notification.notification_type === NotificationType.SUCCESS && <FiCheckCircle className="mr-1 h-3 w-3" />}
                            {notification.notification_type === NotificationType.WARNING && <FiAlertCircle className="mr-1 h-3 w-3" />}
                            {notification.notification_type === NotificationType.ERROR && <FiXCircle className="mr-1 h-3 w-3" />}
                            {notification.notification_type}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">กลุ่มเป้าหมาย:</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTargetBadgeColor(notification.target_users)}`}>
                            <FiUsers className="mr-1 h-3 w-3" />
                            {getTargetLabel(notification.target_users)}
                          </span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">สร้างโดย:</span>
                          <span className="text-gray-900">{notification.created_by}</span>
                        </div>

                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">วันที่ส่ง:</span>
                          <span className="text-gray-900">
                            {notification.sent_at ? formatDate(notification.sent_at) : '-'}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Desktop Table View - Hidden on Mobile */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          หัวเรื่อง
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ประเภท
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          กลุ่มเป้าหมาย
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                          สถานะ
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          สร้างโดย
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          วันที่ส่ง
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedNotifications.map((notification, index) => (
                        <motion.tr
                          key={notification.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="hover:bg-gray-50"
                        >
                          {/* Title & Message */}
                          <td className="px-6 py-4">
                            <div className="max-w-md">
                              <div className="text-sm font-medium text-gray-900">
                                {notification.title}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {notification.message}
                              </div>
                            </div>
                          </td>

                          {/* Type */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeBadgeColor(notification.notification_type)}`}>
                              {notification.notification_type === NotificationType.INFO && <FiInfo className="mr-1 h-3 w-3" />}
                              {notification.notification_type === NotificationType.SUCCESS && <FiCheckCircle className="mr-1 h-3 w-3" />}
                              {notification.notification_type === NotificationType.WARNING && <FiAlertCircle className="mr-1 h-3 w-3" />}
                              {notification.notification_type === NotificationType.ERROR && <FiXCircle className="mr-1 h-3 w-3" />}
                              {notification.notification_type}
                            </span>
                          </td>

                          {/* Target Users */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTargetBadgeColor(notification.target_users)}`}>
                              <FiUsers className="mr-1 h-3 w-3" />
                              {getTargetLabel(notification.target_users)}
                            </span>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {notification.is_sent ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <FiCheckCircle className="mr-1 h-3 w-3" />
                                ส่งแล้ว
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <FiAlertCircle className="mr-1 h-3 w-3" />
                                รอดำเนินการ
                              </span>
                            )}
                          </td>

                          {/* Created By */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {notification.created_by}
                          </td>

                          {/* Sent At */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {notification.sent_at ? formatDate(notification.sent_at) : '-'}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-4 md:px-6 py-4 border-t border-gray-200">
                    {/* Mobile Pagination - Simplified */}
                    <div className="flex md:hidden items-center justify-between">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        ก่อนหน้า
                      </Button>
                      <span className="text-sm text-gray-700">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        ถัดไป
                      </Button>
                    </div>

                    {/* Desktop Pagination - Full */}
                    <div className="hidden md:flex items-center justify-between">
                      <div className="text-sm text-gray-700">
                        แสดง {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredNotifications.length)} จาก {filteredNotifications.length} รายการ
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                        >
                          ก่อนหน้า
                        </Button>
                        <div className="flex items-center space-x-1">
                          {Array.from({ length: totalPages }, (_, i) => i + 1)
                            .filter(page => {
                              return page === 1 ||
                                     page === totalPages ||
                                     (page >= currentPage - 1 && page <= currentPage + 1);
                            })
                            .map((page, index, array) => {
                              if (index > 0 && array[index - 1] !== page - 1) {
                                return (
                                  <React.Fragment key={`ellipsis-${page}`}>
                                    <span className="px-2 text-gray-500">...</span>
                                    <button
                                      onClick={() => setCurrentPage(page)}
                                      className={`px-3 py-1 rounded ${
                                        currentPage === page
                                          ? 'bg-blue-600 text-white'
                                          : 'bg-white text-gray-700 hover:bg-gray-100'
                                      }`}
                                    >
                                      {page}
                                    </button>
                                  </React.Fragment>
                                );
                              }
                              return (
                                <button
                                  key={page}
                                  onClick={() => setCurrentPage(page)}
                                  className={`px-3 py-1 rounded ${
                                    currentPage === page
                                      ? 'bg-blue-600 text-white'
                                      : 'bg-white text-gray-700 hover:bg-gray-100'
                                  }`}
                                >
                                  {page}
                                </button>
                              );
                            })}
                        </div>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                        >
                          ถัดไป
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Create Notification Modal */}
        <CreateNotificationModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSuccess={loadNotifications}
          token={token || ''}
        />
      </div>
    </AdminLayout>
  );
};

export default NotificationsManagement;
